import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Typography,
} from '@mui/material';
import type { RunOptions } from '../../api/scheduledJobs';

interface RunOptionsDialogProps {
  open: boolean;
  jobDisplayName: string;
  /** Job type id (e.g. "company_completion_agent") — controls which options show. */
  jobType?: string;
  defaultEnvironment: string;
  defaultEmailEnabled: boolean;
  onClose: () => void;
  onRun: (options: RunOptions) => void;
}

type RunMode = 'days' | 'company' | 'health_check';
type CompanyScope = 'public' | 'non_public' | 'all';
type CoveragePriorityFilter = 'high' | 'medium' | 'low' | 'all';

const DEFAULT_HEALTH_CHECK_COMPANIES = 10;
const DEFAULT_LATEST_NEWS_LOOKBACK = 2;

const RunOptionsDialog: React.FC<RunOptionsDialogProps> = ({
  open,
  jobDisplayName,
  jobType,
  defaultEnvironment,
  defaultEmailEnabled,
  onClose,
  onRun,
}) => {
  const isGapAgent = jobType === 'pr_coverage_gap_agent';
  const isLatestNewsAgent = jobType === 'latest_news_agent';
  const isCompletionAgent =
    jobType === 'company_completion_agent' ||
    isGapAgent ||
    isLatestNewsAgent;
  const showsDryRun = isCompletionAgent && !isLatestNewsAgent;

  const [environment, setEnvironment] = useState(defaultEnvironment);
  const [mode, setMode] = useState<RunMode>(
    isCompletionAgent ? 'health_check' : 'days'
  );
  const [daysBack, setDaysBack] = useState<number>(
    isLatestNewsAgent ? DEFAULT_LATEST_NEWS_LOOKBACK : 7
  );
  const [companyId, setCompanyId] = useState('');
  const [maxCompanies, setMaxCompanies] = useState<number>(
    DEFAULT_HEALTH_CHECK_COMPANIES
  );
  const [scope, setScope] = useState<CompanyScope>('public');
  const [coveragePriority, setCoveragePriority] =
    useState<CoveragePriorityFilter>('high');
  const [dryRun, setDryRun] = useState(isGapAgent);
  const [workers, setWorkers] = useState(12);
  const [enableUrlRepairCheck, setEnableUrlRepairCheck] = useState(true);
  const [sendEmail, setSendEmail] = useState(defaultEmailEnabled);
  const [overwrite, setOverwrite] = useState(false);
  const [ignoreLastRun, setIgnoreLastRun] = useState(false);

  useEffect(() => {
    if (open) {
      setEnvironment(defaultEnvironment);
      setSendEmail(defaultEmailEnabled);
      setMode(isCompletionAgent ? 'health_check' : 'days');
      setDaysBack(isLatestNewsAgent ? DEFAULT_LATEST_NEWS_LOOKBACK : 7);
      setCompanyId('');
      setMaxCompanies(DEFAULT_HEALTH_CHECK_COMPANIES);
      setScope('public');
      setCoveragePriority('high');
      setDryRun(isGapAgent);
      setWorkers(12);
      setEnableUrlRepairCheck(true);
      setOverwrite(false);
      setIgnoreLastRun(false);
    }
  }, [
    open,
    defaultEnvironment,
    defaultEmailEnabled,
    isCompletionAgent,
    isGapAgent,
    isLatestNewsAgent,
  ]);

  const handleRun = () => {
    const options: RunOptions = {
      environment,
      send_email: sendEmail,
    };
    if (mode === 'company' && companyId.trim()) {
      options.company_id = companyId.trim();
    } else if (mode === 'days') {
      // Full sweep: process every company in the selected scope (no YAML cap).
      // Backend treats max_per_run=0 as unlimited when explicitly provided.
      if (isCompletionAgent) {
        options.max_per_run = 0;
      }
      if (!isGapAgent && !isLatestNewsAgent) {
        options.days_back = daysBack;
      }
    } else if (mode === 'health_check') {
      options.max_per_run = maxCompanies;
    }
    if (isLatestNewsAgent) {
      // Lookback applies to every run mode for this agent.
      options.days_back = daysBack;
      options.scope = scope;
      options.coverage_priority = coveragePriority;
    } else if (isGapAgent) {
      options.scope = scope;
      options.coverage_priority = coveragePriority;
      options.dry_run = dryRun;
      options.workers = workers;
      options.enable_url_repair_check = enableUrlRepairCheck;
    } else if (isCompletionAgent) {
      options.scope = scope;
      options.dry_run = dryRun;
    } else {
      options.overwrite = overwrite;
      options.ignore_last_run = ignoreLastRun;
    }
    onRun(options);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Run Now: {jobDisplayName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Environment</InputLabel>
            <Select
              value={environment}
              label="Environment"
              onChange={(e) =>
                setEnvironment(e.target.value as 'sandbox' | 'production')
              }
            >
              <MenuItem value="sandbox">Sandbox</MenuItem>
              <MenuItem value="production">Production</MenuItem>
            </Select>
          </FormControl>

          {isCompletionAgent && (
            <FormControl size="small" fullWidth>
              <InputLabel>Company scope</InputLabel>
              <Select
                value={scope}
                label="Company scope"
                onChange={(e) => setScope(e.target.value as CompanyScope)}
              >
                <MenuItem value="public">
                  Public companies only (corporate status or Quartr ID)
                </MenuItem>
                <MenuItem value="non_public">
                  Other companies only (non-public)
                </MenuItem>
                <MenuItem value="all">All companies</MenuItem>
              </Select>
            </FormControl>
          )}

          {(isGapAgent || isLatestNewsAgent) && (
            <FormControl size="small" fullWidth>
              <InputLabel>Coverage priority</InputLabel>
              <Select
                value={coveragePriority}
                label="Coverage priority"
                onChange={(e) =>
                  setCoveragePriority(e.target.value as CoveragePriorityFilter)
                }
              >
                <MenuItem value="high">High priority only</MenuItem>
                <MenuItem value="medium">Medium priority only</MenuItem>
                <MenuItem value="low">Low priority only</MenuItem>
                <MenuItem value="all">All priorities</MenuItem>
              </Select>
            </FormControl>
          )}

          {isLatestNewsAgent && (
            <TextField
              type="number"
              label="Lookback days"
              value={daysBack}
              onChange={(e) =>
                setDaysBack(Math.max(1, parseInt(e.target.value, 10) || 1))
              }
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 730 }}
              helperText="Find press releases published in the last N days (typically 1–2)."
            />
          )}

          {showsDryRun && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                  />
                }
                label={
                  isGapAgent
                    ? 'Dry run (report only — never write Neo4j)'
                    : 'Dry run (assess only — save no changes)'
                }
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 4, display: 'block' }}
              >
                {isGapAgent
                  ? 'Gap analysis never ingests PRs. Dry run keeps URL-repair discovery report-only.'
                  : 'When on: report proposed URL fixes, enrichment and backfill without writing to the database. Turn off to apply changes.'}
              </Typography>
            </Box>
          )}

          {isGapAgent && (
            <>
              <TextField
                type="number"
                label="Workers"
                value={workers}
                onChange={(e) =>
                  setWorkers(
                    Math.min(24, Math.max(1, parseInt(e.target.value, 10) || 12))
                  )
                }
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 24 }}
                helperText="Concurrent companies (higher = faster; 12 recommended)."
              />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableUrlRepairCheck}
                      onChange={(e) =>
                        setEnableUrlRepairCheck(e.target.checked)
                      }
                    />
                  }
                  label="URL repair check (after unreachable listings)"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  Off is much faster for smoke runs. On finds replacement feed
                  URLs when the listing times out or 404s.
                </Typography>
              </Box>
            </>
          )}

          {isLatestNewsAgent && (
            <Typography variant="caption" color="text.secondary">
              This agent only discovers recent PRs. Nothing is written to Neo4j
              until you review and approve in the Completion Dashboard.
            </Typography>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Run mode
            </Typography>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as RunMode)}
            >
              {isCompletionAgent && (
                <FormControlLabel
                  value="health_check"
                  control={<Radio />}
                  label="Health check (sample companies)"
                />
              )}
              <FormControlLabel
                value="days"
                control={<Radio />}
                label={
                  isLatestNewsAgent
                    ? 'Full sweep'
                    : isCompletionAgent
                      ? 'Full sweep'
                      : 'Days back'
                }
              />
              <FormControlLabel
                value="company"
                control={<Radio />}
                label="Single company"
              />
            </RadioGroup>
            {mode === 'health_check' && (
              <>
                <TextField
                  type="number"
                  label="Max companies"
                  value={maxCompanies}
                  onChange={(e) =>
                    setMaxCompanies(
                      Math.max(1, parseInt(e.target.value, 10) || 1)
                    )
                  }
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  inputProps={{ min: 1, max: 500 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {isLatestNewsAgent
                    ? 'Scan up to this many in-scope companies for recent PRs.'
                    : isGapAgent
                      ? 'Process up to this many in-scope companies (inactive last). Useful for smoke-testing without a full sweep.'
                      : 'Process up to this many companies that need work (worst first). Useful for smoke-testing the agent without a full sweep.'}
                </Typography>
              </>
            )}
            {mode === 'days' && !isGapAgent && !isLatestNewsAgent && (
              <TextField
                type="number"
                label={isCompletionAgent ? 'Backfill days' : 'Days to look back'}
                value={daysBack}
                onChange={(e) =>
                  setDaysBack(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                inputProps={{ min: 1 }}
              />
            )}
            {mode === 'days' && isCompletionAgent && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Processes <strong>every company in the selected scope</strong>{' '}
                (public ≈1.2k, non-public, or all ≈7k). Ignores the YAML
                max_per_run cap. This can take a long time.
              </Typography>
            )}
            {mode === 'company' && (
              <TextField
                label="Company ID"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                placeholder="Use company ID from DB or URL (not name)"
              />
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label="Send email digest"
          />

          {!isCompletionAgent && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                  />
                }
                label="Replace existing milestones (overwrite)"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={ignoreLastRun}
                    onChange={(e) => setIgnoreLastRun(e.target.checked)}
                  />
                }
                label="Process by milestones only (ignore last run time)"
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={mode === 'company' && !companyId.trim()}
        >
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RunOptionsDialog;
